/**
 * RequireAuth component - ensures user is authenticated before rendering children
 * Redirects unauthenticated users to login
 */
import { useAuth } from '@/hooks/useAuth'
import { PageShell } from '@/components/common/PageShell'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface RequireAuthProps {
  children: React.ReactNode
}

/**
 * Loading state while checking authentication
 */
function AuthLoading() {
  return (
    <PageShell>
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    </PageShell>
  )
}

/**
 * Login prompt for unauthenticated users
 */
function LoginPrompt() {
  const { login } = useAuth()

  // Signing in is a navigation to `/bff/login`, not a call that can fail and return: the page is on its
  // way to the identity provider by the time this handler is finished.
  const handleLogin = () => login()

  return (
    <PageShell>
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        <div className="text-center space-y-6 max-w-md">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Authentication Required</h1>
            <p className="text-muted-foreground">
              Please sign in to access Ignix67.
            </p>
          </div>
          <Button size="lg" onClick={handleLogin}>
            Sign in
          </Button>
        </div>
      </div>
    </PageShell>
  )
}

/**
 * Session failure — as distinct from being signed out.
 *
 * A gateway that cannot answer `/bff/user` is not the same as a browser with no session, and rendering it
 * as a sign-in prompt sends the user round a loop that cannot end: they sign in, come back, and are asked
 * to sign in again.
 */
function AuthUnavailable({ error }: { error: Error }) {
  return (
    <PageShell>
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        <div className="text-center space-y-4 max-w-md">
          <h1 className="text-2xl font-bold">Sign-in is unavailable</h1>
          <p className="text-muted-foreground">
            We could not check your session. This is a problem on our side, not with your account.
          </p>
          <p className="text-xs text-muted-foreground">{error.message}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      </div>
    </PageShell>
  )
}

/**
 * Gates the authenticated half of the app on the BFF session.
 *
 * Three outcomes, not two: still asking, signed out, or the question itself failed. The third used to be
 * folded into the second — which reads as "please sign in" when the truth is "we cannot tell".
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { isInitialized, isAuthenticated, isLoading, error } = useAuth()

  if (!isInitialized || isLoading) {
    return <AuthLoading />
  }

  if (error !== null) {
    return <AuthUnavailable error={error} />
  }

  if (!isAuthenticated) {
    return <LoginPrompt />
  }

  return <>{children}</>
}
